/**
 * Navigation Utility Functions
 *
 * Functions for checking visibility and filtering navigation items.
 *
 * @see Issue #431 - Gap analysis and fixes between Service 23 locks and RBAC permissions
 */

import type { RoutePermissionConfig } from './route-permissions';
import type { LockConfig, NavItem, NavSection, VisibilityConfig, VisibilityContext } from './types';

/**
 * Admin roles that bypass all permission checks
 * @see Issue #428 - Route-level RBAC
 */
const ADMIN_ROLES = new Set(['super_admin', 'platform_admin']);

/**
 * Check if a visibility rule is satisfied given a context
 */
export function checkVisibility(rule: VisibilityConfig | undefined, context: VisibilityContext): boolean {
  if (!rule) return true;

  // Admin roles bypass all permission and role checks (UI only - backend enforces actual security)
  if (context.role && ADMIN_ROLES.has(context.role)) {
    // Still check capabilities and feature flags as those may be business-specific
    // But skip permission and role checks
    if (rule.requireCapability && !context.capabilities?.[rule.requireCapability]) {
      return false;
    }
    if (rule.requireAnyCapability?.length) {
      const hasAny = rule.requireAnyCapability.some((cap) => context.capabilities?.[cap]);
      if (!hasAny) return false;
    }
    if (rule.requireAllCapabilities?.length) {
      const hasAll = rule.requireAllCapabilities.every((cap) => context.capabilities?.[cap]);
      if (!hasAll) return false;
    }
    if (rule.requireFeatureFlag && !context.featureFlags?.[rule.requireFeatureFlag]) {
      return false;
    }
    return true;
  }

  // Check single capability requirement
  if (rule.requireCapability) {
    if (!context.capabilities?.[rule.requireCapability]) {
      return false;
    }
  }

  // Check ANY capability (OR logic)
  if (rule.requireAnyCapability?.length) {
    const hasAny = rule.requireAnyCapability.some((cap) => context.capabilities?.[cap]);
    if (!hasAny) {
      return false;
    }
  }

  // Check ALL capabilities (AND logic)
  if (rule.requireAllCapabilities?.length) {
    const hasAll = rule.requireAllCapabilities.every((cap) => context.capabilities?.[cap]);
    if (!hasAll) {
      return false;
    }
  }

  // Check single role requirement
  if (rule.requireRole) {
    if (context.role !== rule.requireRole) {
      return false;
    }
  }

  // Check ANY role (OR logic)
  if (rule.requireAnyRole?.length) {
    if (!rule.requireAnyRole.includes(context.role ?? '')) {
      return false;
    }
  }

  // Check permission (CASL)
  if (rule.requirePermission && context.ability) {
    const { action, subject } = rule.requirePermission;
    if (!context.ability.can(action, subject)) {
      return false;
    }
  }

  // Check ANY permission (OR logic)
  if (rule.requireAnyPermission?.length && context.ability) {
    const hasAny = rule.requireAnyPermission.some(({ action, subject }) => context.ability.can(action, subject));
    if (!hasAny) {
      return false;
    }
  }

  // ============================================================================
  // Permission String Checks (Issue #428 - Route-level RBAC)
  // ============================================================================

  // Check single permission string (e.g., 'agents.read')
  if (rule.requirePermissionString && context.checkPermission) {
    if (!context.checkPermission(rule.requirePermissionString)) {
      return false;
    }
  }

  // Check ANY permission string (OR logic)
  if (rule.requireAnyPermissionString?.length && context.checkAnyPermission) {
    if (!context.checkAnyPermission(rule.requireAnyPermissionString)) {
      return false;
    }
  }

  // Check ALL permission strings (AND logic)
  if (rule.requireAllPermissionStrings?.length && context.checkAllPermissions) {
    if (!context.checkAllPermissions(rule.requireAllPermissionStrings)) {
      return false;
    }
  }

  // Check feature flag
  if (rule.requireFeatureFlag) {
    if (!context.featureFlags?.[rule.requireFeatureFlag]) {
      return false;
    }
  }

  // Check hideIf (inverse logic)
  if (rule.hideIf) {
    if (checkVisibility(rule.hideIf, context)) {
      return false;
    }
  }

  return true;
}

/**
 * Filter navigation items based on visibility context
 */
export function filterNavItems(items: NavItem[], context: VisibilityContext): NavItem[] {
  return items
    .filter((item) => checkVisibility(item.visibility, context))
    .map((item) => ({
      ...item,
      tabs: item.tabs?.filter((tab) => checkVisibility(tab.visibility, context)),
    }));
}

/**
 * Filter navigation sections based on visibility context
 */
export function filterNavSections(sections: NavSection[], context: VisibilityContext): NavSection[] {
  return sections
    .filter((section) => checkVisibility(section.visibility, context))
    .map((section) => ({
      ...section,
      items: filterNavItems(section.items, context),
    }))
    .filter((section) => section.items.length > 0); // Remove empty sections
}

// ============================================================================
// Type Adapters (Issue #431 - Config Conversion Utilities)
// ============================================================================

/**
 * Convert LockConfig to VisibilityConfig
 *
 * Use this when you want to hide nav items based on the same rules as lock configs.
 *
 * @example
 * ```ts
 * const lockConfig: LockConfig = { permission: 'agents:read' };
 * const visibility = lockConfigToVisibility(lockConfig);
 * // Result: { requirePermissionString: 'agents:read' }
 * ```
 */
export function lockConfigToVisibility(config: LockConfig): VisibilityConfig {
  const result: VisibilityConfig = {};

  if (config.permission) {
    result.requirePermissionString = config.permission;
  }
  if (config.anyPermission?.length) {
    result.requireAnyPermissionString = config.anyPermission;
  }
  if (config.allPermissions?.length) {
    result.requireAllPermissionStrings = config.allPermissions;
  }
  // Note: requiredTier and requiredLicense don't have direct VisibilityConfig equivalents
  // These are handled by LockConfig/LockState for showing lock icons rather than hiding

  return result;
}

/**
 * Convert RoutePermissionConfig to VisibilityConfig
 *
 * Use this to create nav visibility rules that match route permission rules.
 *
 * @example
 * ```ts
 * const routeConfig: RoutePermissionConfig = {
 *   path: '/settings/webhooks',
 *   requiredAnyPermission: ['settings:webhooks:create', 'settings:webhooks:update'],
 * };
 * const visibility = routePermissionToVisibility(routeConfig);
 * // Result: { requireAnyPermissionString: ['settings:webhooks:create', 'settings:webhooks:update'] }
 * ```
 */
export function routePermissionToVisibility(config: RoutePermissionConfig): VisibilityConfig {
  const result: VisibilityConfig = {};

  if (config.requiredPermission) {
    result.requirePermissionString = config.requiredPermission;
  }
  if (config.requiredAnyPermission?.length) {
    result.requireAnyPermissionString = config.requiredAnyPermission;
  }
  if (config.requiredAllPermissions?.length) {
    result.requireAllPermissionStrings = config.requiredAllPermissions;
  }
  if (config.requiredRoles?.length) {
    result.requireAnyRole = config.requiredRoles;
  }

  return result;
}

/**
 * Convert VisibilityConfig to LockConfig
 *
 * Use this to create lock icons based on the same rules as visibility configs.
 *
 * @example
 * ```ts
 * const visibility: VisibilityConfig = { requireAnyPermissionString: ['agents:read'] };
 * const lockConfig = visibilityToLockConfig(visibility);
 * // Result: { anyPermission: ['agents:read'] }
 * ```
 */
export function visibilityToLockConfig(config: VisibilityConfig): LockConfig {
  const result: LockConfig = {};

  if (config.requirePermissionString) {
    result.permission = config.requirePermissionString;
  }
  if (config.requireAnyPermissionString?.length) {
    result.anyPermission = config.requireAnyPermissionString;
  }
  if (config.requireAllPermissionStrings?.length) {
    result.allPermissions = config.requireAllPermissionStrings;
  }
  // Note: capabilities, roles, feature flags don't have LockConfig equivalents

  return result;
}

/**
 * Convert RoutePermissionConfig to LockConfig
 *
 * Use this to create lock icons based on route permission rules.
 *
 * @example
 * ```ts
 * const routeConfig: RoutePermissionConfig = {
 *   path: '/settings/webhooks',
 *   requiredAnyPermission: ['settings:webhooks:create'],
 * };
 * const lockConfig = routePermissionToLockConfig(routeConfig);
 * // Result: { anyPermission: ['settings:webhooks:create'] }
 * ```
 */
export function routePermissionToLockConfig(config: RoutePermissionConfig): LockConfig {
  const result: LockConfig = {};

  if (config.requiredPermission) {
    result.permission = config.requiredPermission;
  }
  if (config.requiredAnyPermission?.length) {
    result.anyPermission = config.requiredAnyPermission;
  }
  if (config.requiredAllPermissions?.length) {
    result.allPermissions = config.requiredAllPermissions;
  }

  return result;
}

/**
 * Check if LockConfig and VisibilityConfig are equivalent
 *
 * Use this to validate that lock and visibility rules are in sync.
 *
 * @example
 * ```ts
 * const lock: LockConfig = { permission: 'agents:read' };
 * const visibility: VisibilityConfig = { requirePermissionString: 'agents:read' };
 * const isEquivalent = areLockAndVisibilityEquivalent(lock, visibility);
 * // Result: true
 * ```
 */
export function areLockAndVisibilityEquivalent(lock: LockConfig, visibility: VisibilityConfig): boolean {
  // Check single permission
  const lockPerm = lock.permission ?? null;
  const visPerm = visibility.requirePermissionString ?? null;
  if (lockPerm !== visPerm) return false;

  // Check any permission
  const lockAny = lock.anyPermission ?? [];
  const visAny = visibility.requireAnyPermissionString ?? [];
  if (lockAny.length !== visAny.length) return false;
  if (!lockAny.every((p) => visAny.includes(p))) return false;

  // Check all permissions
  const lockAll = lock.allPermissions ?? [];
  const visAll = visibility.requireAllPermissionStrings ?? [];
  if (lockAll.length !== visAll.length) return false;
  if (!lockAll.every((p) => visAll.includes(p))) return false;

  return true;
}
